<template>
  <div class="bg-black text-white">
    <Loader v-if="loading" />
    <main v-if="series">
      <!-- Hero Banner -->
      <section class="relative h-[200px] md:h-[60vh]">
        <img
          class="w-full h-full object-cover"
          :src="series.heroImage"
          :alt="series.title"
        />
        <div class="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
        
        <!-- Desktop Content Overlay (md and up) - Absolutely positioned over image -->
        <div class="hidden md:block absolute bottom-0 left-0 p-8 w-full">
          <h1 class="text-6xl font-bold mb-4">{{ series.title }}</h1>
          <div class="flex items-center space-x-2 text-sm mb-4">
            <span>•</span>
            <span class="capitalize" v-for="genre in series.genres">{{ genre }}</span>
          </div>
          <p class="text-gray-300 max-w-2xl mb-6">{{ series.longDescription }}</p>
        </div>
      </section>

      <!-- Mobile Content (below md) - Positioned after the image -->
      <div class="flex flex-col md:hidden mt-10 p-4 pb-8 w-full">
        <div class="py-2 space-x-2">
          <span class="capitalize text-xs bg-orange-600 px-3 py-1 rounded-full" v-for="genre in series.genres">{{ genre }}</span>
          <span v-if="firstEpisode?.releaseDate" class="capitalize text-xs bg-white bg-opacity-10 px-3 py-1 rounded-full">{{ new Date(firstEpisode.releaseDate).getFullYear() }}</span>
          <span v-if="firstEpisode?.rating" class="capitalize text-xs bg-white bg-opacity-10 px-3 py-1 rounded-full">{{ firstEpisode.rating.rating }} rated</span>
        </div>
        <div class="text-sm font-semibold my-2">{{ series.title }}</div>
        <div class="text-xs text-[var(--color-gray-400)] mb-6"> {{ epCount }} Episodes</div>
        
        <!-- Watch Now Section -->
        <div class="text-left text-white text-sm font-bold pb-2 pt-4">
          Watch Now
        </div>

        <!-- Watch Free with Ads Button -->
        <button v-if="firstEpisode" @click="goToEpisode(firstEpisode)"
          class="w-full flex flex-row items-center justify-between h-10 space-x-2 px-8 py-3 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-md transition-colors">
          <span class="font-semibold capitalize">Watch Free with Ads</span>
          <font-awesome-icon icon="play" />
        </button>
        
        <div class="text-sm font-bold mb-2 pt-6">Synopsis</div>
        <p class="text-gray-300 mb-4 text-sm">{{ series.longDescription }}</p>
        
        <div class="text-xs text-[var(--color-gray-400)] mb-2 pt-6">Type</div>
        <div class="text-white mb-8 text-sm">Series</div>
      </div>

      <!-- Episodes Section -->
      <section class="container mx-auto px-4 py-4 md:py-8">
        <div v-for="season in series.seasons" :key="season.seasonNumber" class="mb-8 md:mb-6">
          <h2 class="text-xl md:text-2xl font-bold text-orange-500 mb-4">Season {{ season.seasonNumber }}</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-4">
            <div v-for="episode in season.episodes" :key="episode.id" 
              class="relative group cursor-pointer hover:opacity-80 transition-opacity" 
              @click="goToEpisode(episode)">
              <img :src="episode.thumbnail" :alt="episode.title" class="w-full aspect-video object-cover rounded-lg">
              <div class="flex justify-between items-center mt-2">
                <h3 class="text-sm md:text-base font-medium">{{ episode.episodeNumber }} - {{ episode.title }}</h3>
                <p class="text-xs md:text-sm text-gray-400 whitespace-nowrap ml-2">{{ formatDuration(episode.duration) }}</p>
              </div>
              <p class="mt-1 md:mt-2 text-xs md:text-sm opacity-80 italic line-clamp-2">{{ episode.shortDescription }}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useVideoStore } from '../store/videoStore'
import Loader from '../components/Loader.vue';
import { formatDuration } from '../utils/formatDuration'
import { generateSlug } from '../utils/slugUtils';
import { useAdStore } from '../store/adStore'
import { analyticsEvent } from '../utils/analytics';
import { markUserInitiatedPlayerEntry } from '../utils/playerAutoplayIntent';
components: {
  Loader
}

const route = useRoute()
const router = useRouter()
const videoStore = useVideoStore()
const adStore = useAdStore()
const series = ref(null)
const loading = ref(true)
const defaultFeedId = import.meta.env.VITE_DEFAULT_FEED_ID

// Get the first available episode from the first season
const firstEpisode = computed(() => {
  return series.value?.seasons?.[0]?.episodes?.[0] || null
})

const epCount = computed(() => {
  return series.value?.seasons?.reduce((acc, season) => acc + season.episodes?.length || 0, 0) || 0
})

onMounted(async () => {
  videoStore.selectVideo(null)
  loading.value = true
  const partnerId = route.params.partnerId
  const feedId = partnerId || defaultFeedId
  await videoStore.loadVideos(feedId)

  const slug = route.params.slug
  series.value = videoStore.selectSeries(slug)
  adStore.setupVideoToLookFor(series.value?.seasons?.[0]?.episodes?.[0])
  adStore.setupRequestMethod(adStore.requestMethods.GET)
  if (!series.value) {
    router.push({ name: '404' })
  }
  loading.value = false
})

function goToEpisode(episode) {
  analyticsEvent(episode.title, "beacon", "play_clicked");

  const partnerId = route.params.partnerId
  const slug = generateSlug(episode.title, episode.id)
  markUserInitiatedPlayerEntry()
  router.push({ name: 'video-player', params: { partnerId, slug }, query: { ...route.query } });
  videoStore.selectVideo(episode)
}

</script>
